import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StancerPaymentModal from '../../../components/hubup/StancerPaymentModal';
import {
  confirmWebCreatorProductCheckout,
  createWebCreatorProductCheckout,
} from '../../../api/webCreator';
import { formatProductPriceEur } from '../utils/campaignWebCreator';
import { Field, inputClass } from './BackgroundFields';

function formatEur(price) {
  return `${formatProductPriceEur(price)} TTC`;
}

export default function ProductPurchaseModal({ open, onClose, checkoutSlug, product }) {
  const [step, setStep] = useState('form');
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [city, setCity] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [stancerPayment, setStancerPayment] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const reset = useCallback(() => {
    setStep('form');
    setEmail('');
    setFullName('');
    setAddress('');
    setPostalCode('');
    setCity('');
    setLoading(false);
    setError('');
    setStancerPayment(null);
    setConfirming(false);
  }, []);

  useEffect(() => {
    if (!open) {
      reset();
      return undefined;
    }
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open, reset]);

  if (!open || !product) return null;

  const {
    compId,
    campaignId,
    productId,
    title,
    description,
    imageUrl,
    price,
    buttonLabel,
  } = product;

  const handleCheckout = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await createWebCreatorProductCheckout(checkoutSlug, {
        campaignId,
        productId,
        componentId: compId,
        email: email.trim(),
        fullName: fullName.trim(),
        address: address.trim(),
        postalCode: postalCode.trim(),
        city: city.trim(),
      });
      const data = res?.data;
      if (!res?.success || !data?.paymentUrl) {
        throw new Error(data?.message || 'Impossible de démarrer le paiement.');
      }
      setStancerPayment({
        paymentId: data.paymentId,
        paymentUrl: data.paymentUrl,
        confirmationToken: data.confirmationToken,
      });
      setStep('payment');
    } catch (err) {
      setError(err.message || 'Erreur lors de la commande.');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentAuthorized = async (paymentId) => {
    if (confirming || !stancerPayment?.confirmationToken) return;
    setConfirming(true);
    setError('');
    try {
      const res = await confirmWebCreatorProductCheckout({
        paymentId,
        confirmationToken: stancerPayment.confirmationToken,
      });
      if (!res?.success) {
        throw new Error(res?.data?.message || 'Confirmation impossible.');
      }
      setStancerPayment(null);
      setStep('success');
    } catch (err) {
      setError(err.message || 'Erreur de confirmation.');
    } finally {
      setConfirming(false);
    }
  };

  const modal = (
    <div
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-card border border-border shadow-elevation-3 overflow-hidden flex flex-col max-h-[92vh]">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 shrink-0">
          <h3 className="font-semibold text-foreground flex items-center gap-2">
            <Icon name="ShoppingBag" size={18} />
            {step === 'success' ? 'Merci !' : 'Finaliser votre achat'}
          </h3>
          <button type="button" onClick={onClose} className="p-2 rounded-lg hover:bg-muted">
            <Icon name="X" size={16} />
          </button>
        </div>

        {step === 'form' ? (
          <form onSubmit={handleCheckout} className="p-4 space-y-4 overflow-y-auto flex-1">
            <div className="flex gap-3 rounded-xl border border-border bg-muted/20 p-3">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt=""
                  className="w-16 h-16 rounded-lg object-cover shrink-0 border border-border"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center shrink-0">
                  <Icon name="Image" size={20} className="text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="font-medium text-foreground truncate">{title || 'Produit'}</p>
                {description ? (
                  <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">{description}</p>
                ) : null}
                <p className="text-sm font-semibold text-primary mt-1 tabular-nums">
                  {formatEur(price)}
                </p>
              </div>
            </div>

            <Field label="Nom complet">
              <input
                required
                className={inputClass}
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jean Dupont"
                autoComplete="name"
              />
            </Field>

            <Field label="E-mail">
              <input
                type="email"
                required
                className={inputClass}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                autoComplete="email"
              />
            </Field>

            <div className="space-y-3 pt-1 border-t border-border">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Adresse de facturation
              </p>

              <Field label="Adresse">
                <input
                  required
                  className={inputClass}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="12 rue de la Paix"
                  autoComplete="street-address"
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Code postal">
                  <input
                    required
                    className={inputClass}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                    placeholder="75001"
                    autoComplete="postal-code"
                  />
                </Field>
                <Field label="Ville">
                  <input
                    required
                    className={inputClass}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Paris"
                    autoComplete="address-level2"
                  />
                </Field>
              </div>
            </div>

            {error ? <p className="text-xs text-destructive">{error}</p> : null}

            <div className="flex gap-2 pt-1">
              <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
                Annuler
              </Button>
              <Button type="submit" className="flex-1" disabled={loading}>
                {loading ? '…' : buttonLabel || 'Payer'}
              </Button>
            </div>

            <p className="text-[11px] text-center text-muted-foreground flex items-center justify-center gap-1">
              <Icon name="Lock" size={12} />
              Paiement sécurisé par Stancer
            </p>
          </form>
        ) : null}

        {step === 'success' ? (
          <div className="p-6 text-center space-y-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto">
              <Icon name="CheckCircle" size={32} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-semibold text-foreground">Paiement confirmé</p>
              <p className="text-sm text-muted-foreground mt-1">
                Un récapitulatif a été envoyé à votre adresse e-mail.
              </p>
            </div>
            <Button onClick={onClose} className="w-full">
              Fermer
            </Button>
          </div>
        ) : null}
      </div>

      {step === 'payment' && stancerPayment ? (
        <StancerPaymentModal
          iframeUrl={stancerPayment.paymentUrl}
          paymentId={stancerPayment.paymentId}
          onAuthorized={handlePaymentAuthorized}
          onError={(msg) => setError(msg)}
          onClose={() => {
            setStancerPayment(null);
            setStep('form');
          }}
        />
      ) : null}

      {confirming ? (
        <div className="fixed inset-0 z-[1400] flex items-center justify-center bg-black/40">
          <div className="rounded-xl bg-card px-6 py-4 flex items-center gap-3 shadow-lg">
            <Icon name="Loader2" size={20} className="animate-spin text-primary" />
            <span className="text-sm text-foreground">Confirmation du paiement…</span>
          </div>
        </div>
      ) : null}
    </div>
  );

  return createPortal(modal, document.body);
}
